import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Scrollbar from "../scrollbar";
import { MdCheckCircleOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { getAllProviderData_ } from "../../utils/apis/func";
import Loader from "../Loader";
import EmptyState from "./emptyState";
import { FaSearch } from "react-icons/fa";

const PENDING_URL =
  "https://api.pureworker.com/api/admin/providers?isVerified=review";

/** Everyone waiting on a verification decision, freelancer or business.
 *
 *  Uses axios, like every other list in the panel, and that is load-bearing.
 *  Auth moved to an httpOnly cookie, so nothing writes `AuthToken` to
 *  localStorage any more and every call site builds `Bearer null`. The API
 *  rejects a malformed bearer BEFORE it looks at the cookie, so the request
 *  fails 401 while the operator is perfectly well logged in. Two things save
 *  the axios calls: the global interceptor in index.tsx strips a `Bearer null`
 *  header, and `axios.defaults.withCredentials` sends the cookie.
 *
 *  Raw `fetch` gets neither. That is why the old Freelancers and Businesses
 *  lists (the only two `fetch` call sites in the panel) were always empty while
 *  Completed and Declined worked: the tab badge went out over axios and
 *  succeeded, the list went out over fetch and 401'd, and the failure was
 *  swallowed into "No verification requests".
 *
 *  Two more things this replaces:
 *
 *  1. Separate Freelancers and Businesses tabs. They were the same queue split
 *     by account type, and the split hid work: Businesses sat on 0 while
 *     someone still had to remember to check it. One queue, one number.
 *
 *  2. A tab badge and a list that each fetched independently and could
 *     disagree. The badge read `totalRecords`, the list read `results`, and the
 *     panel showed "Freelancers 5" above "NO VERIFICATION REQUESTS" with no way
 *     to tell which was lying. Both now come from ONE response, and if the
 *     server ever does report a count with no rows, that says so on screen
 *     instead of rendering an empty state that looks like "nothing to do".
 */
interface Props {
  /** Reported up so the tab badge shows exactly what this list holds. */
  onLoaded?: (info: { shown: number; total: number }) => void;
}

const PendingVerifications = ({ onLoaded }: Props) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 10;
  const totalItems = rows?.length || 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const itemsToShow = rows.slice(startIndex, endIndex);

  const filtered =
    searchTerm.length > 0
      ? itemsToShow.filter(
          (item: any) =>
            item?.firstName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
            item?.lastName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
            item?.businessName?.toLowerCase()?.includes(searchTerm.toLowerCase())
        )
      : itemsToShow;

  const handleGet = async (id: any) => {
    setLoading(true);
    const res = await getAllProviderData_(id);
    if (res?.status === 200 || res?.status === 201) navigate(`/verification/${id}`);
    setLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios({
        method: "get",
        url: PENDING_URL,
        headers: { Authorization: `Bearer ${localStorage.getItem("AuthToken")}` },
      });
      const page = res?.data?.providers;
      const results: any[] = Array.isArray(page?.results) ? page.results : [];
      const count = Number(page?.totalRecords ?? results.length) || 0;
      setRows(results);
      setTotal(count);
      onLoaded?.({ shown: results.length, total: count });
    } catch (err: any) {
      /* A failure used to fall through to `|| []`, so a rejected request and an
       * empty queue looked identical. Say which it was. */
      const status = err?.response?.status;
      setError(
        status === 401 || status === 403
          ? "The server rejected this request. Sign out and back in, and tell an engineer if it persists."
          : status
          ? `The server returned ${status}. This does not mean there are no requests.`
          : `Could not reach the server (${err?.message || "network error"}).`
      );
      setRows([]);
      onLoaded?.({ shown: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  /* The exact shape of the reported bug: a count with nothing behind it. */
  const countWithoutRows = !loading && !error && total > 0 && rows.length === 0;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="ml-10 flex items-center sm:flex">
          <FaSearch className="text-[#88087B]" />
          <input
            type="text"
            className="rounded-full ml-4 px-4 py-2 sm:w-48 lg:w-70 font-roboto text-sm font-normal leading-5 tracking-tighter text-[#8E8E93] focus:outline-none"
            placeholder="Search pending verifications"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={load}
          className="mr-10 rounded-full border border-[#88087B] px-4 py-1 font-roboto text-sm text-[#88087B]"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mx-10 mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 font-roboto text-sm text-red-700">
          {error}
        </div>
      )}

      {countWithoutRows && (
        <div className="mx-10 mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 font-roboto text-sm text-amber-800">
          The server reports {total} pending verification
          {total === 1 ? "" : "s"} but returned no rows for this page. Nothing is
          missing from the queue itself, so this is a problem with the request,
          not with the requests.
        </div>
      )}

      {rows.length < 1 && !loading && !error && !countWithoutRows && (
        <EmptyState
          title={"No Verification Requests"}
          text={"Your Verification Request would be displayed here"}
        />
      )}

      {rows.length > 0 && (
        <div className="">
          <section className="">
            <div className="overflow-x-auto rounded-t-lg">
              <table className="w-full">
                <thead className="bg-[#88087B] text-[#F7F7F7] font-roboto text-md font-medium">
                  <tr>
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Phone Number</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item: any) => (
                    <tr
                      key={item.id || item._id}
                      onClick={async () => {
                        await handleGet(item?.id || item?._id);
                      }}
                      className="hover:bg-[#87878d] transition duration-300 font-roboto text-xs font-medium leading-normal text-center cursor-pointer"
                    >
                      <td className="py-2 px-4">
                        {item.firstName
                          ? `${item.firstName} ${item.lastName}`
                          : item.businessName}
                      </td>
                      {/* The two queues are merged, so which one this is still
                          has to be visible. */}
                      <td className="py-2 px-4 capitalize">
                        {item.accountType || "freelancer"}
                      </td>
                      <td className="py-2 px-4">{item?.phoneNumber}</td>
                      <td className="py-2 px-4">{item.email}</td>
                      <td className="py-2 px-4">
                        <MdCheckCircleOutline size={20} className="text-[#88087B]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Scrollbar
              currentPage={currentPage}
              totalPages={Math.ceil(totalItems / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPrevPageClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              onNextPageClick={() =>
                currentPage < Math.ceil(totalItems / itemsPerPage) &&
                setCurrentPage(currentPage + 1)
              }
            />
          </section>
        </div>
      )}
      {loading && <Loader />}
    </>
  );
};

export default PendingVerifications;
